import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { loadCityLocations } from '../api/Location';
import { searchPackages } from '../api/Packages';
import { fetchCustomerById } from '../api/Customer';
import { fetchQueryById } from '../api/Query';
import { LocationOption } from '../types/Location';
import { CustomActivityInclusion, CustomFlight, InsuranceDetail, PackageOption, TransferPricing, VisaDetail } from '../types/PackageBuilder';
import { Client, ItineraryItem, TripInfo, TravellerInfo } from '../types/Quotation';
import { PackageCabPricingRow, TravelPackage } from '../types/TravelPackage';
import { QuotationRoomOccupancy } from '../components/QuotationBuilder/quotationRoomOccupancy';
import { countWords } from '../utils/text';
import { useHotelAutoPoints } from './useHotelAutoPoints';
import { useHotelCache } from './useHotelCache';
import { useItinerary } from './useItinerary';
import { usePackageBuilderDerived } from './usePackageBuilderDerived';
import { usePackagePrefill } from './usePackagePrefill';
import { useQuotationRepricing } from './useQuotationRepricing';
import { BreakdownCategoryTotals, BuilderMode, FaqLimits, HighlightLimits, PriceOverrideKey, PriceOverrides } from '../components/PackageBuilder/contracts';
import { fromQueryRooms, toTravellerInfoFromRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';

type UsePackageBuilderStateArgs = {
  builderMode: BuilderMode;
  sourceQueryId?: number;
  packageToCopy?: TravelPackage;
  packageToEdit?: TravelPackage;
  packageFromDraft?: unknown;
  hotelRooms?: QuotationRoomOccupancy[];
  highlightLimits: HighlightLimits;
  faqLimits: FaqLimits;
};

type Addon = { name: string; price: number; currency: string; description: string; quoteBasePrice?: number };
type Review = { date: string; user: string; rating: number; comment: string };
type FaqEntry = { question: string; answer: string };

export type UsePackageBuilderStateResult = {
  coreState: {
    tripInfo: TripInfo;
    client: Client;
    quotationSource: string;
    quotationCreatedAt?: string;
    nightsCount: number;
    itinerary: ItineraryItem[];
    editingId: string | null;
    queryCustomerId?: number;
    queryAssignedId?: number;
    queryCreatedBy?: number;
    placeDraft: string;
    placeDraftValue: string;
    nightsDraft: number;
    itineraryPointDrafts: Record<number, string>;
  };
  pricingState: {
    pricePer: 'person' | 'package' | 'night';
    currency: 'INR' | 'USD';
    isPublished: boolean;
    rating: string;
    strikePrice: number;
    quotationBasePrice: number;
    pricePerPerson: number;
    offerPrice: number;
    markupType: 'percent' | 'fixed';
    markupValue: number;
    transferPricing: TransferPricing;
    priceOverrides: PriceOverrides;
    quotationTransferOverride: number | null;
    quotationFinalOverride: number | null;
  };
  contentState: {
    highlights: string[];
    highlightDraft: string;
    highlightError: string;
    mealPlan: string;
    transferType: string;
    idealFor: string[];
    hotelCategory: string;
    idealVendor: string;
    searchTags: string[];
    relatedDestinations: string[];
    relatedPackages: string[];
    visaDetails: VisaDetail[];
    insuranceDetails: InsuranceDetail[];
    customVisaDetails: VisaDetail[];
    customInsuranceDetails: InsuranceDetail[];
    customFlights: CustomFlight[];
    customActivityInclusions: CustomActivityInclusion[];
    exclusions: string[];
    importantTitleDraft: string;
    importantDescriptionDraft: string;
    importantInfoError: string;
    addons: Addon[];
    reviews: Review[];
    faq: FaqEntry[];
  };
  modalState: {
    showVisaModal: boolean;
    showInsuranceModal: boolean;
    showCustomVisaModal: boolean;
    showCustomInsuranceModal: boolean;
    showCustomFlightModal: boolean;
  };
  assetsState: {
    heroImageFile: File | null;
    heroImagePreview: string;
    galleryFiles: File[];
    hotelCache: ReturnType<typeof useHotelCache>['hotelCache'];
    hotelImageFiles: Record<string, File | null>;
  };
  quotationPackageState: {
    packageId: string | null;
    transferRows: PackageCabPricingRow[];
  };
  quotationRepriceState: {
    flightReselectRequired: boolean;
    flightReselectTrigger: number;
    hotelRepriceWarnings: string[];
    isHotelRepricing: boolean;
    markFlightsReselected: () => void;
  };
  derivedState: {
    cities: string[];
    slug: string;
    descriptionWordCount: number;
    addonsTotalForPackage: number;
    calculatedFlightsTotal: number;
    calculatedHotelsTotal: number;
    calculatedTransfersTotal: number;
    calculatedVisaTotal: number;
    calculatedInsuranceTotal: number;
    calculatedActivitiesTotal: number;
    calculatedAddonsTotalForPackage: number;
    quotationTransferCalculation: ReturnType<typeof usePackageBuilderDerived>['quotationTransferCalculation'];
    insuranceTotal: number;
    visaTotal: number;
    flightsTotal: number;
    hotelsTotal: number;
    activitiesTotal: number;
    transfersTotal: number;
    inclusionsTotal: number;
    markupAmount: number;
    finalTotal: number;
    displayedTotal: number;
    currencySymbol: string;
    importantTitleCount: number;
    importantAnswerWordCount: number;
    canAddImportantInfo: boolean;
    defaultHotelStartDate: string;
    totalPax: number;
    hotelMinStars: number;
    hotelAutoPointsByDay: string[][];
  };
  actions: {
    setClient: React.Dispatch<React.SetStateAction<Client>>;
    setQuotationSource: React.Dispatch<React.SetStateAction<string>>;
    setItinerary: React.Dispatch<React.SetStateAction<ItineraryItem[]>>;
    setPricePer: React.Dispatch<React.SetStateAction<'person' | 'package' | 'night'>>;
    setCurrency: React.Dispatch<React.SetStateAction<'INR' | 'USD'>>;
    setIsPublished: React.Dispatch<React.SetStateAction<boolean>>;
    setRating: React.Dispatch<React.SetStateAction<string>>;
    setStrikePrice: React.Dispatch<React.SetStateAction<number>>;
    setQuotationBasePrice: React.Dispatch<React.SetStateAction<number>>;
    setPricePerPerson: React.Dispatch<React.SetStateAction<number>>;
    setOfferPrice: React.Dispatch<React.SetStateAction<number>>;
    setMarkupType: React.Dispatch<React.SetStateAction<'percent' | 'fixed'>>;
    setMarkupValue: React.Dispatch<React.SetStateAction<number>>;
    setTransferPricing: React.Dispatch<React.SetStateAction<TransferPricing>>;
    setQuotationPackageId: React.Dispatch<React.SetStateAction<string | null>>;
    setQuotationTransferRows: React.Dispatch<React.SetStateAction<PackageCabPricingRow[]>>;
    setQuotationTransferOverride: React.Dispatch<React.SetStateAction<number | null>>;
    setQuotationFinalOverride: React.Dispatch<React.SetStateAction<number | null>>;
    setPriceOverride: (key: PriceOverrideKey, value: number | null) => void;
    handleTripInfoChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: unknown }) => void;
    addPlace: () => void;
    removePlace: (idx: number) => void;
    movePlace: (idx: number, direction: 'up' | 'down') => void;
    setPlaceField: (idx: number, field: 'label' | 'value' | 'nights', value: unknown) => void;
    loadCityOptions: (inputValue: string) => Promise<LocationOption[]>;
    loadPackageOptions: (inputValue: string) => Promise<PackageOption[]>;
    setDayTitle: (dayIndex: number, title: string) => void;
    setDayPoints: (dayIndex: number, points: string[]) => void;
    addItineraryPoint: (dayIndex: number) => void;
    removeItineraryPoint: (dayIndex: number, pointIndex: number) => void;
    addHighlight: () => void;
    removeHighlight: (idx: number) => void;
    addImportantInfo: () => void;
    removeImportantInfo: (idx: number) => void;
    toggleInclusion: (value: string) => void;
    toggleIdealFor: (value: string) => void;
    onHeroFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onGalleryFilesChange: (e: ChangeEvent<HTMLInputElement>) => void;
    removeCustomFlight: (index: number) => void;
    formatFlightLabel: (flight: CustomFlight) => string;
    setMealPlan: React.Dispatch<React.SetStateAction<string>>;
    setTransferType: React.Dispatch<React.SetStateAction<string>>;
    setIdealVendor: React.Dispatch<React.SetStateAction<string>>;
    setSearchTags: React.Dispatch<React.SetStateAction<string[]>>;
    setHotelCategory: React.Dispatch<React.SetStateAction<string>>;
    setPlaceDraft: React.Dispatch<React.SetStateAction<string>>;
    setPlaceDraftValue: React.Dispatch<React.SetStateAction<string>>;
    setNightsDraft: React.Dispatch<React.SetStateAction<number>>;
    setItineraryPointDrafts: React.Dispatch<React.SetStateAction<Record<number, string>>>;
    setHighlightDraft: React.Dispatch<React.SetStateAction<string>>;
    setHighlightError: React.Dispatch<React.SetStateAction<string>>;
    setHighlights: React.Dispatch<React.SetStateAction<string[]>>;
    setImportantTitleDraft: React.Dispatch<React.SetStateAction<string>>;
    setImportantDescriptionDraft: React.Dispatch<React.SetStateAction<string>>;
    setImportantInfoError: React.Dispatch<React.SetStateAction<string>>;
    setRelatedDestinations: React.Dispatch<React.SetStateAction<string[]>>;
    setRelatedPackages: React.Dispatch<React.SetStateAction<string[]>>;
    setIdealFor: React.Dispatch<React.SetStateAction<string[]>>;
    setExclusions: React.Dispatch<React.SetStateAction<string[]>>;
    setAddons: React.Dispatch<React.SetStateAction<Addon[]>>;
    setReviews: React.Dispatch<React.SetStateAction<Review[]>>;
    setFaq: React.Dispatch<React.SetStateAction<FaqEntry[]>>;
    setVisaDetails: React.Dispatch<React.SetStateAction<VisaDetail[]>>;
    setInsuranceDetails: React.Dispatch<React.SetStateAction<InsuranceDetail[]>>;
    setCustomVisaDetails: React.Dispatch<React.SetStateAction<VisaDetail[]>>;
    setCustomInsuranceDetails: React.Dispatch<React.SetStateAction<InsuranceDetail[]>>;
    setCustomFlights: React.Dispatch<React.SetStateAction<CustomFlight[]>>;
    setCustomActivityInclusions: React.Dispatch<React.SetStateAction<CustomActivityInclusion[]>>;
    setShowVisaModal: React.Dispatch<React.SetStateAction<boolean>>;
    setShowInsuranceModal: React.Dispatch<React.SetStateAction<boolean>>;
    setShowCustomVisaModal: React.Dispatch<React.SetStateAction<boolean>>;
    setShowCustomInsuranceModal: React.Dispatch<React.SetStateAction<boolean>>;
    setShowCustomFlightModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSuppressedHotelPoints: React.Dispatch<React.SetStateAction<Record<number, string[]>>>;
    setPriceOverrides: React.Dispatch<React.SetStateAction<PriceOverrides>>;
  };
};

export const usePackageBuilderState = ({
  builderMode,
  sourceQueryId,
  packageToCopy,
  packageToEdit,
  packageFromDraft,
  hotelRooms,
  highlightLimits,
  faqLimits,
}: UsePackageBuilderStateArgs): UsePackageBuilderStateResult => {
  const [tripInfo, setTripInfo] = useState<TripInfo>({
    title: '',
    subtitle: '',
    tripOverview: '',
    from: undefined,
    places: [],
    startDate: '',
    travellers: { adults: 1, children: 0, infants: 0 },
    inclusions: [],
    totalPrice: 0,
  });
  const [client, setClient] = useState<Client>({
    name: '',
    phoneNumber: '',
    email: '',
  });
  const [quotationSource, setQuotationSource] = useState('');
  const [quotationCreatedAt, setQuotationCreatedAt] = useState<string | undefined>(undefined);
  const nightsCount = tripInfo.places.reduce((sum, p) => sum + (p.nights || 0), 0);
  const [itinerary, setItinerary] = useItinerary(nightsCount);

  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string>('');
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [pricePer, setPricePer] = useState<'person' | 'package' | 'night'>('person');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');
  const [isPublished, setIsPublished] = useState(false);
  const [rating, setRating] = useState('');
  const [strikePrice, setStrikePrice] = useState<number>(0);
  const [quotationBasePrice, setQuotationBasePrice] = useState<number>(0);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [highlightDraft, setHighlightDraft] = useState('');
  const [highlightError, setHighlightError] = useState('');
  const [mealPlan, setMealPlan] = useState('None');
  const [transferType, setTransferType] = useState('');
  const [transferPricing, setTransferPricing] = useState<TransferPricing>({ shared: null, private: null });
  const [quotationTransferRows, setQuotationTransferRows] = useState<PackageCabPricingRow[]>([]);
  const [quotationTransferOverride, setQuotationTransferOverride] = useState<number | null>(null);
  const [quotationFinalOverride, setQuotationFinalOverride] = useState<number | null>(null);
  const [quotationPackageId, setQuotationPackageId] = useState<string | null>(null);
  const [idealFor, setIdealFor] = useState<string[]>([]);
  const [hotelCategory, setHotelCategory] = useState('');
  const [idealVendor, setIdealVendor] = useState('');
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [relatedDestinations, setRelatedDestinations] = useState<string[]>([]);
  const [relatedPackages, setRelatedPackages] = useState<string[]>([]);
  const [pricePerPerson, setPricePerPerson] = useState<number>(0);
  const [offerPrice, setOfferPrice] = useState<number>(0);
  const [markupType, setMarkupType] = useState<'percent' | 'fixed'>('percent');
  const [markupValue, setMarkupValue] = useState<number>(0);
  const [suppressedHotelPoints, setSuppressedHotelPoints] = useState<Record<number, string[]>>({});
  const [visaDetails, setVisaDetails] = useState<VisaDetail[]>([]);
  const [insuranceDetails, setInsuranceDetails] = useState<InsuranceDetail[]>([]);
  const [customVisaDetails, setCustomVisaDetails] = useState<VisaDetail[]>([]);
  const [customInsuranceDetails, setCustomInsuranceDetails] = useState<InsuranceDetail[]>([]);
  const [customFlights, setCustomFlights] = useState<CustomFlight[]>([]);
  const [customActivityInclusions, setCustomActivityInclusions] = useState<CustomActivityInclusion[]>([]);
  const [showVisaModal, setShowVisaModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showCustomVisaModal, setShowCustomVisaModal] = useState(false);
  const [showCustomInsuranceModal, setShowCustomInsuranceModal] = useState(false);
  const [showCustomFlightModal, setShowCustomFlightModal] = useState(false);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [importantTitleDraft, setImportantTitleDraft] = useState('');
  const [importantDescriptionDraft, setImportantDescriptionDraft] = useState('');
  const [importantInfoError, setImportantInfoError] = useState('');
  const [addons, setAddons] = useState<Addon[]>([]);
  const [reviews, setReviews] = useState<Array<{ date: string; user: string; rating: number; comment: string }>>([]);
  const [faq, setFaq] = useState<Array<{ question: string; answer: string }>>([]);
  const [priceOverrides, setPriceOverrides] = useState<PriceOverrides>({
    flights: null,
    hotels: null,
    transfers: null,
    visa: null,
    insurance: null,
    activities: null,
    addons: null,
  });
  const [persistedBreakdown, setPersistedBreakdown] = useState<BreakdownCategoryTotals | null>(null);
  const [initialLiveTotals, setInitialLiveTotals] = useState<BreakdownCategoryTotals | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [queryCustomerId, setQueryCustomerId] = useState<number | undefined>(undefined);
  const [queryAssignedId, setQueryAssignedId] = useState<number | undefined>(undefined);
  const [queryCreatedBy, setQueryCreatedBy] = useState<number | undefined>(undefined);
  const [placeDraft, setPlaceDraft] = useState('');
  const [placeDraftValue, setPlaceDraftValue] = useState('');
  const [nightsDraft, setNightsDraft] = useState<number>(1);
  const [itineraryPointDrafts, setItineraryPointDrafts] = useState<Record<number, string>>({});

  const { hotelCache } = useHotelCache(
    tripInfo,
    builderMode === 'package' ? 2 : undefined,
    builderMode === 'package' ? mealPlan : undefined,
    builderMode === 'quotation' ? hotelRooms : undefined
  );
  const [hotelImageFiles] = useState<Record<string, File | null>>({});
  const resolvedCityCodeRef = useRef<Map<string, string | null>>(new Map());

  useEffect(() => {
    (async () => {
      try {
        if (builderMode === 'quotation' && sourceQueryId) {
          const q = await fetchQueryById(sourceQueryId);
          const cid = (q as any)?.customerId || (q as any)?.customer?.id;
          if (cid) setQueryCustomerId(Number(cid));

          const assignedRaw = (q as any)?.assigned_to ?? (q as any)?.assignedTo?.id;
          if (assignedRaw !== undefined && assignedRaw !== null) {
            setQueryAssignedId(Number(assignedRaw));
          }

          const createdRaw = (q as any)?.created_by;
          if (createdRaw !== undefined && createdRaw !== null) {
            setQueryCreatedBy(Number(createdRaw));
          }
          const sourceRaw = (q as any)?.source;
          if (sourceRaw && !quotationSource) {
            setQuotationSource(String(sourceRaw));
          }
          const customerName = (q as any)?.customer?.name;
          if (customerName) {
            setClient((prev) => (prev.name ? prev : { ...prev, name: String(customerName) }));
          }
          const queryDate = (q as any)?.queryDate || (q as any)?.query_date;
          if (queryDate) {
            setTripInfo((prev) => (prev.startDate ? prev : { ...prev, startDate: String(queryDate).slice(0, 10) }));
          }
          const rawRooms = (q as any)?.rooms;
          const hasQueryRooms = Array.isArray(rawRooms) && rawRooms.length > 0;
          const queryRooms = hasQueryRooms ? fromQueryRooms(rawRooms) : [];
          const roomTravellers: TravellerInfo | null = hasQueryRooms ? toTravellerInfoFromRooms(queryRooms) : null;
          const pax = Number((q as any)?.pax || 0);
          if (roomTravellers && (roomTravellers.adults > 0 || roomTravellers.children > 0)) {
            setTripInfo((prev) => {
              const total = Number(prev.travellers?.adults || 0)
                + Number(prev.travellers?.children || 0)
                + Number(prev.travellers?.infants || 0);
              if (total > 1) return prev;
              return {
                ...prev,
                travellers: roomTravellers,
              };
            });
          } else if (pax > 0) {
            setTripInfo((prev) => {
              const total = Number(prev.travellers?.adults || 0)
                + Number(prev.travellers?.children || 0)
                + Number(prev.travellers?.infants || 0);
              if (total > 1) return prev;
              return {
                ...prev,
                travellers: { adults: pax, children: 0, infants: 0 },
              };
            });
          }
          if (cid) {
            try {
              const customer = await fetchCustomerById(Number(cid));
              if (customer) {
                setClient((prev) => ({
                  ...prev,
                  name: prev.name || customer.name || '',
                  email: prev.email || customer.email || '',
                  phoneNumber: prev.phoneNumber || customer.phoneNumber || '',
                }));
              }
            } catch {
              // ignore
            }
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [builderMode, sourceQueryId, quotationSource]);

  const prefillState = usePackagePrefill({
    prefillStartDateFromPackage: builderMode === 'package',
    packageFromDraft,
    packageToEdit,
    packageToCopy,
    nightsCount,
    tripInfoStartDate: tripInfo.startDate,
    setItinerary,
    setTripInfo,
    setPriceOverrides,
    setPricePer,
    setCurrency,
    setMarkupType,
    setMarkupValue,
    setRating,
    setHotelCategory,
    setVisaDetails,
    setInsuranceDetails,
    setCustomVisaDetails,
    setCustomInsuranceDetails,
    setCustomFlights,
    setCustomActivityInclusions,
    setStrikePrice,
    setPricePerPerson,
    setOfferPrice,
    setQuotationBasePrice,
    setHighlights,
    setMealPlan,
    setTransferType,
    setTransferPricing,
    setQuotationPackageId,
    setQuotationTransferRows,
    setIdealFor,
    setIdealVendor,
    setSearchTags,
    setRelatedDestinations,
    setRelatedPackages,
    setExclusions,
    setAddons,
    setReviews,
    setFaq,
    setIsPublished,
    setEditingId,
    setQuotationCreatedAt,
    setClient,
    setQuotationSource,
    setPersistedBreakdown,
    setInitialLiveTotals,
  });

  const autoTransferPriceRef = useRef<number | null>(null);
  useEffect(() => {
    if (builderMode !== 'package') return;
    if (!transferType) return;
    const selectedPrice = transferType === 'Private'
      ? transferPricing.private
      : transferPricing.shared;
    if (selectedPrice === null || selectedPrice === undefined) return;
    setPriceOverrides((prev) => {
      const current = prev.transfers;
      if (current !== null && current !== autoTransferPriceRef.current) return prev;
      if (current === selectedPrice) return prev;
      autoTransferPriceRef.current = selectedPrice;
      return { ...prev, transfers: selectedPrice };
    });
  }, [builderMode, transferType, transferPricing.private, transferPricing.shared, setPriceOverrides]);

  useEffect(() => {
    let cancelled = false;
    const isValidCityCode = (value: string) => /^(?:[A-Za-z]{3}|\d+)$/.test(value);

    const resolvePlace = async (label: string, idx: number, currentValue: string) => {
      const cacheKey = label.toLowerCase();
      const cached = resolvedCityCodeRef.current.get(cacheKey);
      if (cached === null) return;
      if (cached) {
        if (cached !== currentValue) {
          setTripInfo((prev) => {
            const place = prev.places[idx];
            if (!place || place.label.toLowerCase() !== label.toLowerCase()) return prev;
            if (isValidCityCode(place.value)) return prev;
            const nextPlaces = prev.places.map((p, i) => (i === idx ? { ...p, value: cached } : p));
            return { ...prev, places: nextPlaces };
          });
        }
        return;
      }

      try {
        const opts = await loadCityLocations(label);
        if (cancelled) return;
        const match = (opts || []).find(
          o => o.type === 'city' && o.label?.toLowerCase().includes(label.toLowerCase())
        );
        if (match?.value) {
          resolvedCityCodeRef.current.set(cacheKey, match.value);
          setTripInfo((prev) => {
            const place = prev.places[idx];
            if (!place || place.label.toLowerCase() !== label.toLowerCase()) return prev;
            if (isValidCityCode(place.value)) return prev;
            const nextPlaces = prev.places.map((p, i) => (i === idx ? { ...p, value: match.value } : p));
            return { ...prev, places: nextPlaces };
          });
        } else {
          resolvedCityCodeRef.current.set(cacheKey, null);
        }
      } catch {
        resolvedCityCodeRef.current.set(cacheKey, null);
      }
    };

    tripInfo.places.forEach((place, idx) => {
      const value = (place.value || '').trim();
      const label = (place.label || place.value || '').trim();
      if (!label || label.length < 3) return;
      if (isValidCityCode(value)) return;
      void resolvePlace(label, idx, value);
    });

    return () => {
      cancelled = true;
    };
  }, [tripInfo.places, setTripInfo]);

  const setPriceOverride = useCallback((key: PriceOverrideKey, value: number | null) => {
    setPriceOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const quotationRepriceState = useQuotationRepricing({
    builderMode,
    tripInfo,
    itinerary,
    setItinerary,
    visaDetails,
    setVisaDetails,
    insuranceDetails,
    setInsuranceDetails,
    addons,
    setAddons,
    setPriceOverrides,
    quotationHotelRooms: builderMode === 'quotation' ? hotelRooms : undefined,
  });

  const handleTripInfoChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }) => {
      const name = 'target' in e ? e.target.name : e.name;
      let value = 'target' in e ? e.target.value : e.value;
      if (name === 'totalPrice') value = Number(value);
      setTripInfo((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const updateTripInfo = useCallback((name: string, value: any) => {
    handleTripInfoChange({ name, value });
  }, [handleTripInfoChange]);

  const addPlace = useCallback(() => {
    const label = placeDraft.trim();
    const value = placeDraftValue.trim() || placeDraft.trim();
    if (!label) return;
    const next = [...tripInfo.places, { label, value, nights: Math.max(1, Number(nightsDraft || 1)) }];
    updateTripInfo('places', next);
    setPlaceDraft('');
    setPlaceDraftValue('');
    setNightsDraft(1);
  }, [nightsDraft, placeDraft, placeDraftValue, tripInfo.places, updateTripInfo]);

  const removePlace = useCallback((idx: number) => {
    updateTripInfo('places', tripInfo.places.filter((_, i) => i !== idx));
  }, [tripInfo.places, updateTripInfo]);

  const movePlace = useCallback((idx: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= tripInfo.places.length) return;
    const next = [...tripInfo.places];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    updateTripInfo('places', next);
  }, [tripInfo.places, updateTripInfo]);

  const setPlaceField = useCallback((idx: number, field: 'label' | 'value' | 'nights', value: any) => {
    const next = tripInfo.places.map((p, i) =>
      i === idx ? { ...p, [field]: field === 'nights' ? Number(value || 0) : value } : p
    );
    updateTripInfo('places', next);
  }, [tripInfo.places, updateTripInfo]);

  const loadCityOptions = async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const result = await loadCityLocations(inputValue);
      const options = result || [];
      if (inputValue.trim() && options.length === 0) {
        return [{
          label: inputValue.trim(),
          value: inputValue.trim(),
          type: 'city'
        }];
      }
      return options;
    } catch {
      if (inputValue.trim()) {
        return [{
          label: inputValue.trim(),
          value: inputValue.trim(),
          type: 'city'
        }];
      }
      return [];
    }
  };

  const loadPackageOptions = async (inputValue: string): Promise<PackageOption[]> => {
    const query = inputValue.trim();
    if (query.length < 4) return [];
    try {
      const results = await searchPackages(query);
      const slugs = (Array.isArray(results) ? results : [])
        .map((pkg: any) => pkg.slug || pkg.package_slug || pkg.packageSlug || pkg?.seo?.slug)
        .filter((slug: string) => !!slug);
      const unique = Array.from(new Set(slugs));
      return unique.map((slug) => ({ label: slug, value: slug }));
    } catch {
      return [];
    }
  };

  const setDayTitle = useCallback((dayIndex: number, title: string) => {
    setItinerary((prev) => prev.map((d, i) => (i === dayIndex ? { ...d, title } : d)));
  }, [setItinerary]);

  const setDayPoints = useCallback((dayIndex: number, points: string[]) => {
    setItinerary((prev) =>
      prev.map((d, i) => (i === dayIndex ? { ...d, points } as any : d))
    );
  }, [setItinerary]);

  const addItineraryPoint = useCallback((dayIndex: number) => {
    const draft = (itineraryPointDrafts[dayIndex] || '').trim();
    if (!draft) return;
    setItinerary((prev) =>
      prev.map((d, i) => {
        if (i !== dayIndex) return d;
        const existing = Array.isArray((d as any).points) ? (d as any).points : [];
        return { ...d, points: [...existing, draft] } as any;
      })
    );
    setItineraryPointDrafts((prev) => ({ ...prev, [dayIndex]: '' }));
  }, [itineraryPointDrafts, setItinerary]);
  const {
    cities,
    slug,
    descriptionWordCount,
    addonsTotalForPackage,
    rawFlightsTotal,
    rawHotelsTotal,
    rawTransfersTotal,
    rawVisaTotal,
    rawInsuranceTotal,
    rawActivitiesTotal,
    rawAddonsTotalForPackage,
    calculatedFlightsTotal,
    calculatedHotelsTotal,
    calculatedTransfersTotal,
    calculatedVisaTotal,
    calculatedInsuranceTotal,
    calculatedActivitiesTotal,
    calculatedAddonsTotalForPackage,
    quotationTransferCalculation,
    insuranceTotal,
    visaTotal,
    flightsTotal,
    hotelsTotal,
    activitiesTotal,
    transfersTotal,
    inclusionsTotal,
    markupAmount,
    finalTotal,
    displayedTotal,
    currencySymbol,
    importantTitleCount,
    importantAnswerWordCount,
    canAddImportantInfo,
    defaultHotelStartDate,
    totalPax,
    hotelMinStars,
  } = usePackageBuilderDerived({
    builderMode,
    tripInfo,
    itinerary,
    nightsCount,
    addons,
    insuranceDetails,
    visaDetails,
    priceOverrides,
    markupType,
    markupValue,
    currency,
    hotelCategory,
    importantTitleDraft,
    importantDescriptionDraft,
    faqLimits,
    persistedBreakdown,
    initialLiveTotals,
    quotationPackageId: quotationPackageId || undefined,
    quotationTransferRows,
    quotationTransferOverride,
    quotationFinalOverride,
  });

  useEffect(() => {
    if (!persistedBreakdown || prefillState.pendingApply || initialLiveTotals) return;
    setInitialLiveTotals({
      flights: rawFlightsTotal,
      hotels: rawHotelsTotal,
      transfers: rawTransfersTotal,
      visa: rawVisaTotal,
      insurance: rawInsuranceTotal,
      activities: rawActivitiesTotal,
      addons: rawAddonsTotalForPackage,
    });
  }, [
    initialLiveTotals,
    persistedBreakdown,
    prefillState.pendingApply,
    rawActivitiesTotal,
    rawAddonsTotalForPackage,
    rawFlightsTotal,
    rawHotelsTotal,
    rawInsuranceTotal,
    rawTransfersTotal,
    rawVisaTotal,
  ]);
  const { hotelAutoPointsByDay } = useHotelAutoPoints({
    itinerary,
    tripInfo,
    suppressedHotelPoints,
    setItinerary,
  });

  const removeItineraryPoint = useCallback((dayIndex: number, pointIndex: number) => {
    let removedPoint = '';
    setItinerary((prev) =>
      prev.map((d, i) => {
        if (i !== dayIndex) return d;
        const existing = Array.isArray((d as any).points) ? (d as any).points : [];
        removedPoint = existing[pointIndex] || '';
        return { ...d, points: existing.filter((_: any, idx: number) => idx !== pointIndex) } as any;
      })
    );
    if (removedPoint && (hotelAutoPointsByDay[dayIndex] || []).includes(removedPoint)) {
      setSuppressedHotelPoints((prev) => {
        const next = { ...prev };
        const existing = new Set(next[dayIndex] || []);
        existing.add(removedPoint);
        next[dayIndex] = Array.from(existing);
        return next;
      });
    }
  }, [setItinerary, hotelAutoPointsByDay]);

  const addHighlight = useCallback(() => {
    const value = highlightDraft.trim();
    if (!value) return;
    if (value.length > highlightLimits.maxChars) {
      setHighlightError(`Highlights must be ${highlightLimits.maxChars} characters or less.`);
      return;
    }
    if (highlights.length >= highlightLimits.max) {
      setHighlightError(`You can add up to ${highlightLimits.max} highlights.`);
      return;
    }
    if (highlights.includes(value)) {
      setHighlightError('Highlight already added.');
      return;
    }
    setHighlights([...highlights, value]);
    setHighlightDraft('');
    setHighlightError('');
  }, [highlightDraft, highlights, highlightLimits]);

  const removeHighlight = useCallback((idx: number) => {
    setHighlights(highlights.filter((_, i) => i !== idx));
  }, [highlights]);

  const addImportantInfo = useCallback(() => {
    const title = importantTitleDraft.trim();
    const description = importantDescriptionDraft.trim();
    if (!title) {
      setImportantInfoError('Title is required.');
      return;
    }
    if (title.length > faqLimits.questionMax) {
      setImportantInfoError(`Question must be ${faqLimits.questionMax} characters or less.`);
      return;
    }
    const answerWordCount = countWords(description);
    if (answerWordCount === 0 || answerWordCount > faqLimits.answerMaxWords) {
      setImportantInfoError(`Answer must be 1-${faqLimits.answerMaxWords} words.`);
      return;
    }
    setFaq((prev) => [...prev, { question: title, answer: description }]);
    setImportantTitleDraft('');
    setImportantDescriptionDraft('');
    setImportantInfoError('');
  }, [importantTitleDraft, importantDescriptionDraft, faqLimits]);

  const removeImportantInfo = useCallback((idx: number) => {
    setFaq((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const toggleInclusion = useCallback((value: string) => {
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions || []);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, inclusions: Array.from(next) };
    });
  }, []);

  const toggleIdealFor = useCallback((value: string) => {
    setIdealFor((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return Array.from(next);
    });
  }, []);

  useEffect(() => {
    if (builderMode !== 'package' && builderMode !== 'quotation') return;
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (mealPlan && mealPlan !== 'None') next.add('meals');
      if (!mealPlan || mealPlan === 'None') next.delete('meals');
      return { ...prev, inclusions: Array.from(next) };
    });
  }, [mealPlan, builderMode]);

  useEffect(() => {
    if (builderMode !== 'package' && builderMode !== 'quotation') return;
    const hasHotels = itinerary.some((d) => (d.hotels || []).length > 0);
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (hasHotels) next.add('hotel'); else next.delete('hotel');
      const nextArr = Array.from(next);
      if (nextArr.length === prev.inclusions.length && nextArr.every((x) => prev.inclusions.includes(x))) return prev;
      return { ...prev, inclusions: nextArr };
    });
  }, [itinerary, builderMode]);

  useEffect(() => {
    if (builderMode !== 'package') return;
    const hasVisa = visaDetails.length > 0;
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (hasVisa) next.add('visa'); else next.delete('visa');
      const nextArr = Array.from(next);
      if (nextArr.length === prev.inclusions.length && nextArr.every((x) => prev.inclusions.includes(x))) return prev;
      return { ...prev, inclusions: nextArr };
    });
  }, [visaDetails, builderMode]);

  useEffect(() => {
    if (builderMode !== 'package') return;
    const hasInsurance = insuranceDetails.length > 0;
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (hasInsurance) next.add('insurance'); else next.delete('insurance');
      const nextArr = Array.from(next);
      if (nextArr.length === prev.inclusions.length && nextArr.every((x) => prev.inclusions.includes(x))) return prev;
      return { ...prev, inclusions: nextArr };
    });
  }, [insuranceDetails, builderMode]);

  useEffect(() => {
    if (builderMode === 'package') {
      if (pricePer !== 'person') {
        setPricePer('person');
      }
      if (offerPrice !== finalTotal) {
        setOfferPrice(finalTotal);
      }
      if (pricePerPerson !== finalTotal) {
        setPricePerPerson(finalTotal);
      }
    }
    if (builderMode !== 'package' && builderMode !== 'quotation') return;
    setTripInfo((prev) => {
      const nextTotal = Number((builderMode === 'quotation' ? displayedTotal : finalTotal) || 0);
      if (prev.totalPrice === nextTotal) return prev;
      return { ...prev, totalPrice: nextTotal };
    });
  }, [displayedTotal, finalTotal, builderMode, pricePer, offerPrice, pricePerPerson]);

  useEffect(() => {
    if (builderMode === 'package') return;
    const hasFlights = itinerary.some((d) => (d.flights || []).length > 0);
    const hasHotels = itinerary.some((d) => (d.hotels || []).length > 0);
    const hasActivities = itinerary.some((d) => (d.activities || []).length > 0);
    const hasTransfers = itinerary.some((d) => (d.transfers || []).length > 0);
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (hasFlights) next.add('flight'); else next.delete('flight');
      if (hasHotels) next.add('hotel'); else next.delete('hotel');
      if (hasActivities) next.add('activity'); else next.delete('activity');
      if (hasTransfers) next.add('transfer'); else next.delete('transfer');
      const nextArr = Array.from(next);
      if (nextArr.length === prev.inclusions.length && nextArr.every((x) => prev.inclusions.includes(x))) return prev;
      return { ...prev, inclusions: nextArr };
    });
  }, [itinerary, builderMode]);

  const onHeroFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setHeroImageFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setHeroImagePreview(url);
    } else {
      setHeroImagePreview('');
    }
  };

  const onGalleryFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setGalleryFiles(files);
  };

  const removeCustomFlight = (index: number) => {
    setCustomFlights((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFlightLabel = (flight: CustomFlight) => {
    const formatAirport = (code?: string, name?: string) => {
      const safeCode = (code || '').trim();
      const safeName = (name || '').trim();
      if (!safeCode && !safeName) return '';
      if (safeName && safeCode) {
        const lowerName = safeName.toLowerCase();
        const lowerCode = safeCode.toLowerCase();
        return lowerName.includes(lowerCode) ? safeName : `${safeName} (${safeCode})`;
      }
      return safeName || safeCode;
    };
    const arrivalLabel = formatAirport(
      flight.arrivalAirportCode || flight.airportCode,
      flight.arrivalAirportName || flight.airportName
    );
    const departureLabel = formatAirport(
      flight.departureAirportCode,
      flight.departureAirportName
    );
    if (arrivalLabel && departureLabel) return `Arrival: ${arrivalLabel} | Departure: ${departureLabel}`;
    if (arrivalLabel) return `Arrival: ${arrivalLabel}`;
    if (departureLabel) return `Departure: ${departureLabel}`;
    return 'Flight';
  };

  return {
    coreState: {
      tripInfo,
      client,
      quotationSource,
      quotationCreatedAt,
      nightsCount,
      itinerary,
      editingId,
      queryCustomerId,
      queryAssignedId,
      queryCreatedBy,
      placeDraft,
      placeDraftValue,
      nightsDraft,
      itineraryPointDrafts,
    },
    pricingState: {
      pricePer,
      currency,
      isPublished,
      rating,
      strikePrice,
      quotationBasePrice,
      pricePerPerson,
      offerPrice,
      markupType,
      markupValue,
      transferPricing,
      priceOverrides,
      quotationTransferOverride,
      quotationFinalOverride,
    },
    contentState: {
      highlights,
      highlightDraft,
      highlightError,
      mealPlan,
      transferType,
      idealFor,
      hotelCategory,
      idealVendor,
      searchTags,
      relatedDestinations,
      relatedPackages,
      visaDetails,
      insuranceDetails,
      customVisaDetails,
      customInsuranceDetails,
      customFlights,
      customActivityInclusions,
      exclusions,
      importantTitleDraft,
      importantDescriptionDraft,
      importantInfoError,
      addons,
      reviews,
      faq,
    },
    modalState: {
      showVisaModal,
      showInsuranceModal,
      showCustomVisaModal,
      showCustomInsuranceModal,
      showCustomFlightModal,
    },
    assetsState: {
      heroImageFile,
      heroImagePreview,
      galleryFiles,
      hotelCache,
      hotelImageFiles,
    },
    quotationPackageState: {
      packageId: quotationPackageId,
      transferRows: quotationTransferRows,
    },
    quotationRepriceState,
    derivedState: {
      cities,
      slug,
      descriptionWordCount,
      addonsTotalForPackage,
      calculatedFlightsTotal,
      calculatedHotelsTotal,
      calculatedTransfersTotal,
      calculatedVisaTotal,
      calculatedInsuranceTotal,
      calculatedActivitiesTotal,
      calculatedAddonsTotalForPackage,
      quotationTransferCalculation,
      insuranceTotal,
      visaTotal,
      flightsTotal,
      hotelsTotal,
      activitiesTotal,
      transfersTotal,
      inclusionsTotal,
      markupAmount,
      finalTotal,
      displayedTotal,
      currencySymbol,
      importantTitleCount,
      importantAnswerWordCount,
      canAddImportantInfo,
      defaultHotelStartDate,
      totalPax,
      hotelMinStars,
      hotelAutoPointsByDay,
    },
    actions: {
      setClient,
      setQuotationSource,
      setItinerary,
      setPricePer,
      setCurrency,
      setIsPublished,
      setRating,
      setStrikePrice,
      setQuotationBasePrice,
      setPricePerPerson,
      setOfferPrice,
      setMarkupType,
      setMarkupValue,
      setTransferPricing,
      setQuotationPackageId,
      setQuotationTransferRows,
      setQuotationTransferOverride,
      setQuotationFinalOverride,
      setPriceOverride,
      handleTripInfoChange,
      addPlace,
      removePlace,
      movePlace,
      setPlaceField,
      loadCityOptions,
      loadPackageOptions,
      setDayTitle,
      setDayPoints,
      addItineraryPoint,
      removeItineraryPoint,
      addHighlight,
      removeHighlight,
      addImportantInfo,
      removeImportantInfo,
      toggleInclusion,
      toggleIdealFor,
      onHeroFileChange,
      onGalleryFilesChange,
      removeCustomFlight,
      formatFlightLabel,
      setMealPlan,
      setTransferType,
      setIdealVendor,
      setSearchTags,
      setHotelCategory,
      setPlaceDraft,
      setPlaceDraftValue,
      setNightsDraft,
      setItineraryPointDrafts,
      setHighlightDraft,
      setHighlightError,
      setHighlights,
      setImportantTitleDraft,
      setImportantDescriptionDraft,
      setImportantInfoError,
      setRelatedDestinations,
      setRelatedPackages,
      setIdealFor,
      setExclusions,
      setAddons,
      setReviews,
      setFaq,
      setVisaDetails,
      setInsuranceDetails,
      setCustomVisaDetails,
      setCustomInsuranceDetails,
      setCustomFlights,
      setCustomActivityInclusions,
      setShowVisaModal,
      setShowInsuranceModal,
      setShowCustomVisaModal,
      setShowCustomInsuranceModal,
      setShowCustomFlightModal,
      setSuppressedHotelPoints,
      setPriceOverrides,
    },
  };
};
